// based on https://github.com/buzzfeed/mono/blob/master/packages/frontend/buzzblocks/js/services/localization/index.js
import cookies from '../cookies';

const editions = {
  de: ['de'],
  en: ['au', 'ca', 'in', 'uk', 'us'],
  es: ['es', 'mx'],
  fr: ['fr'],
  ja: ['jp'],
  pt: ['br'],
};

const optionsDefault = { useCountryCookie: true };
const localizationDefault = { country: 'en-us', language: 'en' };


/**
 * Helper function to check if language/country combo is valid and return the appropriate edition string
 * @param {String} language - two-letter language code
 * @param country - two-letter country code
 * @returns {string}
 */
function getEditionString(language = '', country = '') {
  if (editions[language] && editions[language].includes(country)) {
    return `${language}-${country}`;
  }

  // Special case for a mix of es-es and es-mx
  if (language === 'es') {
    return 'es';
  }

  return 'en-us';
}

/**
 * @typedef {Object} LocalizationInfo
 * @property {String} language - two-letter language code from the post or page context
 * @property {String} country - hyphenated combination of two-letter language code and two-letter country code, e.g. en-us, from the post or page context
 */

/**
 * Returns edition information - language, country, and edition string
 * For NON-EN posts picks language and country based on provided defaults (usually coming from post or page data).
 * For EN posts picks a most suitable language for user's country cookie,
 *   unless {useCountryCookie: false} is passed
 * Defaults to en-us.
 * @param {LocalizationInfo} localization
 * @param {Object} options - whether or not to use user's country cookie when edition is ambiguous
 * @return {String} edition - hyphenated combination of two-letter language code and two-letter country code, e.g. en-us
 */
export function getEdition(localization = {}, options = {}) {
  options = Object.assign({}, optionsDefault, options);
  localization = Object.assign({}, localizationDefault, localization);

  let edition = localization.country;
  let [language, country = ''] = edition.split('-');

  if (localization.language !== 'en' || (localization.language === 'en' && !options.useCountryCookie)) {
    return getEditionString(language, country);
  }

  let userEdition = cookies.get('country');
  if (!userEdition) {
    userEdition = country || 'us';
  }
  [language, country] = userEdition.split('-');

  if (!country && language !== 'es') {
    country = language;
    language = Object.keys(editions).filter(e => editions[e].includes(language))[0];
  }

  return getEditionString(language, country);
}

/**
 * Gets the country cookie that the user is bucketed or geolocated in
 * @return {String} - user country from cookie or 'us' as fallback.
 */
export function getUserCountry() {
  return cookies.get('country') || 'us';
}

/**
 * Gets the country cookie that the user is geolocated in
 * @return {String} - user country from cookie or 'us' as fallback.
 */
export function getUserGeoCountry() {
  return cookies.get('bf-geo-country') || 'US';
}
